import {
	ApiError,
	type PortalCatalogImage,
	type PortalCatalogItem,
	type PortalCatalogItemInput,
	type PortalCatalogSku,
	type PortalChannel,
	type PortalSkuChannel,
} from "@khazzin/api-client";
import type { Translate } from "@khazzin/i18n";
import { formatDate, formatDateTime } from "@khazzin/i18n/dates";
import {
	BarcodeListField,
	cleanBarcodes,
	Form,
	FormErrorSummary,
	FormField,
	MoneyInput,
	useApiForm,
} from "@khazzin/ui/patterns/form";
import { useUpload } from "@khazzin/ui/patterns/ImportDialog";
import { EmptyState, PageHeader } from "@khazzin/ui/patterns/page";
import { StatusChip } from "@khazzin/ui/patterns/StatusChip";
import { type Column, DataTable, useCursorPagination } from "@khazzin/ui/patterns/table";
import {
	Button,
	ConfirmDialog,
	Drawer,
	Field,
	Input,
	NumberInput,
	Select,
	Textarea,
} from "@khazzin/ui/primitives";
import type { Tone } from "@khazzin/ui/styles";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";
import { Can, NotFound } from "../../app/shell/Shell";
import { useStoreScope } from "../../app/shell/scope";
import { levelOf, reaches } from "../../app/shell/sections";

/**
 * The Store Portal's Catalog (EP10-S07, DA-10-07): the store's items with
 * their SKUs, channel badges and approval chip, the channel health strip, and
 * the item drawer — name, description, photos, SKU rows, a default package
 * per SKU and each SKU's channel sync rows.
 *
 * **The server is the rule.** A code another SKU of the store holds comes back
 * as `409` on `skus[i].code` and lands under that row; nothing here checks
 * uniqueness. At `read` every input is read-only and no write is drawn.
 */

export function catalogKey(storeId: string) {
	return ["portal", storeId, "catalog"] as const;
}

/** The purpose a store's product photo is uploaded under, owned by the store. */
export const STORE_ITEM_IMAGE = "store_item_image";
/** A sku carries at most four photos, the first the thumbnail (`01` §3). */
export const MAX_IMAGES = 4;
/** A sku with no currency yet is priced in dollars (owner decision: USD). */
const DEFAULT_CURRENCY = "USD";
/** The Select's "no package of its own" value; Radix refuses an empty one. */
const STORE_DEFAULT = "store_default";

const CHANNEL_TONE: Record<PortalSkuChannel["state"], Tone> = {
	synced: "success",
	drift: "warning",
	failed: "danger",
	conflict: "danger",
};

const HEALTH_TONE: Record<PortalChannel["health"], Tone> = {
	connected: "success",
	degraded: "warning",
	disconnected: "danger",
};

/** How bad each sync state is, for an item's badge: its worst sku wins. */
const SEVERITY: Record<PortalSkuChannel["state"], number> = {
	synced: 0,
	drift: 1,
	failed: 2,
	conflict: 3,
};

/** One badge per channel, carrying the worst state among the item's skus. */
export function channelBadges(
	item: PortalCatalogItem,
): { channel: string; state: PortalSkuChannel["state"] }[] {
	const worst = new Map<string, PortalSkuChannel["state"]>();
	for (const sku of item.skus) {
		for (const row of sku.channels) {
			const held = worst.get(row.channel);
			if (held === undefined || SEVERITY[row.state] > SEVERITY[held]) {
				worst.set(row.channel, row.state);
			}
		}
	}
	return [...worst].map(([channel, state]) => ({ channel, state }));
}

function ApprovalChip({ status, t }: { status: PortalCatalogItem["approval"]; t: Translate }) {
	return (
		<StatusChip tone={status === "approved" ? "success" : "warning"}>
			{t(`portal.catalog.approval.${status}`)}
		</StatusChip>
	);
}

function Thumbnail({ image, alt }: { image: PortalCatalogImage | undefined; alt: string }) {
	return image?.url ? (
		<img
			src={image.url}
			alt={alt}
			className="size-10 rounded-sm border border-border object-cover"
		/>
	) : (
		<span aria-hidden className="block size-10 rounded-sm border border-border bg-bg-subtle" />
	);
}

export function CatalogPage() {
	const { t, locale } = useLocale();
	const api = useApi();
	const { store, context } = useStoreScope();
	const storeId = store?.storeId ?? "";
	const level = levelOf(store, "catalog");
	const timeZone = context?.preferences?.timezone ?? "Asia/Beirut";
	const [open, setOpen] = useState<PortalCatalogItem | "new" | null>(null);
	const page = useCursorPagination<PortalCatalogItem>(
		(cursor, limit, signal) =>
			api
				.listPortalCatalogItems({ limit, signal, ...(cursor === null ? {} : { cursor }) })
				.then((answer) => ({ items: answer.items, nextCursor: answer.nextCursor ?? null })),
		{ queryKey: [...catalogKey(storeId), "items"], enabled: storeId !== "" && level !== "none" },
	);

	if (level === "none") {
		return <NotFound />;
	}

	const columns: Column<PortalCatalogItem>[] = [
		{
			id: "image",
			headerKey: "portal.catalog.columns.image",
			width: 64,
			cell: (r) => <Thumbnail image={r.images[0]} alt={r.name} />,
		},
		{ id: "name", headerKey: "portal.catalog.columns.name", card: "wide", cell: (r) => r.name },
		{
			id: "skus",
			headerKey: "portal.catalog.columns.skus",
			align: "end",
			cell: (r) => r.skus.length,
		},
		{
			id: "channels",
			headerKey: "portal.catalog.columns.channels",
			cell: (r) => (
				<span className="flex flex-wrap gap-1">
					{channelBadges(r).map((b) => (
						<StatusChip key={b.channel} tone={CHANNEL_TONE[b.state]} size="sm">
							{t(`portal.catalog.channel.${b.channel}`)}
						</StatusChip>
					))}
				</span>
			),
		},
		{
			id: "approval",
			headerKey: "portal.catalog.columns.approval",
			cell: (r) => <ApprovalChip status={r.approval} t={t} />,
		},
		{
			id: "updated",
			headerKey: "portal.catalog.columns.updated",
			cell: (r) => formatDate(r.updatedAt, { locale, timeZone }),
		},
	];

	return (
		<div className="flex flex-col gap-4">
			<PageHeader
				title={t("portal.catalog.title")}
				subtitle={t("portal.catalog.subtitle")}
				primaryAction={
					<Can section="catalog" level="write">
						<Button variant="primary" onClick={() => setOpen("new")}>
							{t("portal.catalog.newItem")}
						</Button>
					</Can>
				}
			/>
			{reaches(levelOf(store, "integrations"), "read") && (
				<ChannelStrip storeId={storeId} timeZone={timeZone} />
			)}
			<DataTable<PortalCatalogItem>
				screenKey="portal.catalog.items"
				caption={t("portal.catalog.title")}
				t={t}
				columns={columns}
				rowKey={(r) => r.itemId}
				onRowClick={(r) => setOpen(r)}
				page={{ items: page.page?.items ?? [], nextCursor: page.page?.nextCursor ?? null }}
				status={page.status}
				error={page.error}
				onRetry={page.refetch}
				collapse="cards"
				pagination={{
					showingFrom: page.showingFrom,
					showingTo: page.showingTo,
					hasNext: page.hasNext,
					hasPrevious: page.hasPrevious,
					onNext: page.next,
					onPrevious: page.previous,
				}}
				empty={
					<EmptyState
						title={t("portal.catalog.empty.title")}
						body={t("portal.catalog.empty.body")}
					/>
				}
			/>
			{open !== null && (
				<ItemDrawer
					key={open === "new" ? "new" : open.itemId}
					item={open === "new" ? null : open}
					storeId={storeId}
					writable={level === "write"}
					timeZone={timeZone}
					onClose={() => setOpen(null)}
				/>
			)}
		</div>
	);
}

/** The store's channels and how they are doing. */
function ChannelStrip({ storeId, timeZone }: { storeId: string; timeZone: string }) {
	const { t, locale } = useLocale();
	const api = useApi();
	const channels = useQuery({
		queryKey: [...catalogKey(storeId), "channels"],
		queryFn: ({ signal }) => api.listPortalChannels({ signal }),
		enabled: storeId !== "",
	});
	const rows = channels.data?.items ?? [];
	if (rows.length === 0) {
		return null;
	}
	return (
		<section
			aria-label={t("portal.catalog.health.label")}
			className="flex flex-wrap items-center gap-2"
		>
			{/* Not links (ADJ-M03-4): a store has no screen of its own for its
			    channel connections yet — Khazzin support manages them — and the
			    old link opened Settings, which has no channel tab. */}
			{rows.map((c) => (
				<span key={c.connectionId} className="inline-flex items-center gap-2 text-[12.5px]">
					<StatusChip tone={HEALTH_TONE[c.health]}>
						{t(`portal.catalog.channel.${c.channel}`)} · {t(`portal.catalog.health.${c.health}`)}
					</StatusChip>
					<span className="text-fg-muted">
						{c.lastSyncAt
							? t("portal.catalog.health.lastSync", {
									at: formatDateTime(c.lastSyncAt, { locale, timeZone }),
								})
							: t("portal.catalog.health.never")}
					</span>
				</span>
			))}
		</section>
	);
}

interface SkuRowValue {
	key: string;
	skuId?: string;
	code: string;
	/** One per row of the list editor (ADJ-1022-1). */
	barcodes: string[];
	weightG: string;
	priceMinor: number | null;
	currency: string;
	sku?: PortalCatalogSku;
}

interface ItemValues {
	name: string;
	description: string;
	images: PortalCatalogImage[];
	skus: SkuRowValue[];
}

let rowSeq = 0;

function rowOf(sku?: PortalCatalogSku): SkuRowValue {
	rowSeq += 1;
	return sku === undefined
		? {
				key: `new-${rowSeq}`,
				code: "",
				barcodes: [],
				weightG: "",
				priceMinor: null,
				currency: DEFAULT_CURRENCY,
			}
		: {
				key: sku.skuId,
				skuId: sku.skuId,
				code: sku.code,
				barcodes: [...sku.barcodes],
				weightG: sku.weightG > 0 ? String(sku.weightG) : "",
				priceMinor: sku.unitPriceMinor ?? null,
				currency: sku.currency ?? DEFAULT_CURRENCY,
				sku,
			};
}

/** The form as the API takes it. */
export function toInput(values: ItemValues): PortalCatalogItemInput {
	return {
		name: values.name.trim(),
		description: values.description.trim(),
		imageIds: values.images.map((i) => i.attachmentId),
		skus: values.skus.map((row) => {
			return {
				...(row.skuId === undefined ? {} : { skuId: row.skuId }),
				...(row.code.trim() === "" ? {} : { code: row.code.trim() }),
				barcodes: cleanBarcodes(row.barcodes),
				...(row.weightG.trim() === "" ? {} : { weightG: Number(row.weightG) }),
				...(row.priceMinor === null
					? {}
					: { unitPriceMinor: row.priceMinor, currency: row.currency }),
			};
		}),
	};
}

/** The checks worth a round trip saved; the server checks everything. */
export function validateItem(
	values: ItemValues,
	creating: boolean,
	t: Translate,
): Record<string, string> {
	const errors: Record<string, string> = {};
	if (values.name.trim() === "") {
		errors.name = t("portal.catalog.errors.nameRequired");
	}
	values.skus.forEach((row, i) => {
		// Only a new item's first sku is numbered for the store.
		if (row.code.trim() === "" && row.skuId === undefined && (!creating || i > 0)) {
			errors[`skus[${i}].code`] = t("portal.catalog.errors.codeRequired");
		}
		const weight = Number(row.weightG);
		if (row.weightG.trim() !== "" && (!Number.isInteger(weight) || weight < 0)) {
			errors[`skus[${i}].weightG`] = t("portal.catalog.errors.weight");
		}
	});
	return errors;
}

function ItemDrawer({
	item,
	storeId,
	writable,
	timeZone,
	onClose,
}: {
	item: PortalCatalogItem | null;
	storeId: string;
	writable: boolean;
	timeZone: string;
	onClose: () => void;
}) {
	const { t } = useLocale();
	const api = useApi();
	const queryClient = useQueryClient();
	const creating = item === null;
	const [current, setCurrent] = useState(item);
	const [asking, setAsking] = useState(false);
	const initial: ItemValues = {
		name: item?.name ?? "",
		description: item?.description ?? "",
		images: item?.images ?? [],
		skus: item === null ? [rowOf()] : item.skus.map((s) => rowOf(s)),
	};
	const refresh = () => void queryClient.invalidateQueries({ queryKey: catalogKey(storeId) });
	const form = useApiForm<ItemValues>({
		initial,
		validate: (values) => validateItem(values, creating, t),
		submit: async (values) => {
			const saved =
				current === null
					? await api.createPortalCatalogItem(toInput(values))
					: await api.updatePortalCatalogItem(current.itemId, toInput(values));
			refresh();
			setCurrent(saved);
			form.reset({
				name: saved.name,
				description: saved.description,
				images: saved.images,
				skus: saved.skus.map((s) => rowOf(s)),
			});
		},
	});
	const { values } = form;
	const setRow = (index: number, patch: Partial<SkuRowValue>) =>
		form.set(
			"skus",
			values.skus.map((row, i) => (i === index ? { ...row, ...patch } : row)),
		);
	// A sku's own actions answer with the sku; the row keeps what was typed.
	const skuSaved = (saved: PortalCatalogSku) => {
		refresh();
		form.set(
			"skus",
			values.skus.map((row) => (row.skuId === saved.skuId ? { ...row, sku: saved } : row)),
		);
	};

	return (
		<Drawer
			open
			onOpenChange={(next) => !next && onClose()}
			size="lg"
			title={current === null ? t("portal.catalog.drawer.newTitle") : current.name}
			closeLabel={t("ui.actions.close")}
			dirty={form.dirty}
			discard={{
				title: t("portal.catalog.drawer.discardTitle"),
				confirmLabel: t("portal.catalog.drawer.discard"),
				cancelLabel: t("ui.actions.cancel"),
			}}
			footer={
				writable ? (
					<>
						{/* Cancel asks as Escape does (ADJ-M03-4). */}
						<Button variant="secondary" onClick={() => (form.dirty ? setAsking(true) : onClose())}>
							{t("ui.actions.cancel")}
						</Button>
						<Button variant="primary" loading={form.submitting} onClick={() => form.handleSubmit()}>
							{t("ui.actions.save")}
						</Button>
					</>
				) : null
			}
		>
			<Form form={form} aria-label={t("portal.catalog.drawer.label")}>
				<FormErrorSummary form={form} t={t} />
				<FormField name="name" label={t("portal.catalog.fields.name")} required>
					{(control) => (
						<Input
							{...control}
							readOnly={!writable}
							maxLength={120}
							value={values.name}
							onChange={(e) => form.set("name", e.target.value)}
						/>
					)}
				</FormField>
				<FormField name="description" label={t("portal.catalog.fields.description")} optional>
					{(control) => (
						<Textarea
							{...control}
							rows={3}
							readOnly={!writable}
							maxLength={2000}
							value={values.description}
							onChange={(e) => form.set("description", e.target.value)}
						/>
					)}
				</FormField>
				<FormField name="imageIds" label={t("portal.catalog.fields.images")} optional>
					{() => (
						<ImagePicker
							storeId={storeId}
							images={values.images}
							writable={writable}
							onChange={(images) => form.set("images", images)}
							alt={values.name}
						/>
					)}
				</FormField>
				<section aria-label={t("portal.catalog.skus.label")} className="flex flex-col gap-3">
					<h3 className="text-[14px] font-semibold text-fg">{t("portal.catalog.skus.label")}</h3>
					{values.skus.map((row, i) => (
						<SkuRow
							key={row.key}
							index={i}
							row={row}
							writable={writable}
							codeOptional={creating && i === 0}
							timeZone={timeZone}
							onChange={(patch) => setRow(i, patch)}
							onSaved={skuSaved}
							// ADJ-M03-4: a row added and not yet saved can be taken back;
							// a saved SKU leaves through Delete, not here. An item keeps one.
							{...(writable && row.skuId === undefined && values.skus.length > 1
								? {
										onRemove: () =>
											form.set(
												"skus",
												values.skus.filter((other) => other.key !== row.key),
											),
									}
								: {})}
						/>
					))}
					{writable && (
						<Button
							variant="secondary"
							className="self-start"
							disabled={values.skus.length >= 6}
							onClick={() => form.set("skus", [...values.skus, rowOf()])}
						>
							{t("portal.catalog.skus.add")}
						</Button>
					)}
				</section>
			</Form>
			<ConfirmDialog
				open={asking}
				onOpenChange={setAsking}
				tone="danger"
				title={t("portal.catalog.drawer.discardTitle")}
				confirmLabel={t("portal.catalog.drawer.discard")}
				cancelLabel={t("ui.actions.cancel")}
				onConfirm={() => {
					setAsking(false);
					onClose();
				}}
			/>
		</Drawer>
	);
}

function SkuRow({
	index,
	row,
	writable,
	codeOptional,
	timeZone,
	onChange,
	onSaved,
	onRemove,
}: {
	index: number;
	row: SkuRowValue;
	writable: boolean;
	codeOptional: boolean;
	timeZone: string;
	onChange: (patch: Partial<SkuRowValue>) => void;
	onSaved: (sku: PortalCatalogSku) => void;
	/** Takes an unsaved row back; absent when the row cannot go. */
	onRemove?: () => void;
}) {
	const { t, locale } = useLocale();
	const api = useApi();
	const [problem, setProblem] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const sku = row.sku;
	const field = (name: string) => `skus[${index}].${name}`;

	const act = (call: () => Promise<PortalCatalogSku>) => {
		setBusy(true);
		setProblem(null);
		call()
			.then(onSaved, (err: unknown) =>
				setProblem(err instanceof ApiError ? err.message : t("portal.catalog.errors.failed")),
			)
			.finally(() => setBusy(false));
	};

	return (
		<fieldset className="flex flex-col gap-3 rounded-md border border-border p-3">
			<legend className="px-1 text-[12.5px] text-fg-muted">
				{sku?.variant || t("portal.catalog.skus.row", { n: index + 1 })}
			</legend>
			{onRemove !== undefined && (
				<Button variant="ghost" size="sm" className="self-end" onClick={onRemove}>
					{t("portal.catalog.skus.remove")}
				</Button>
			)}
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
				<FormField
					name={field("code")}
					label={t("portal.catalog.fields.code")}
					{...(codeOptional
						? { optional: true, hint: t("portal.catalog.fields.codeHint") }
						: { required: true })}
				>
					{(control) => (
						<Input
							{...control}
							className="font-mono"
							readOnly={!writable}
							maxLength={64}
							value={row.code}
							onChange={(e) => onChange({ code: e.target.value })}
						/>
					)}
				</FormField>
				<FormField
					name={field("barcodes")}
					label={t("portal.catalog.fields.barcodes")}
					hint={t("portal.catalog.fields.barcodesHint")}
					optional
				>
					{(control) => (
						<BarcodeListField
							{...control}
							value={row.barcodes}
							disabled={!writable}
							onChange={(barcodes) => onChange({ barcodes })}
						/>
					)}
				</FormField>
				<FormField name={field("weightG")} label={t("portal.catalog.fields.weight")} optional>
					{(control) => (
						<NumberInput
							{...control}
							integerOnly
							unit={t("portal.catalog.fields.grams")}
							readOnly={!writable}
							value={row.weightG}
							onChange={(e) => onChange({ weightG: e.target.value })}
						/>
					)}
				</FormField>
				<FormField name={field("unitPriceMinor")} label={t("portal.catalog.fields.price")} optional>
					{(control) => (
						<MoneyInput
							{...control}
							value={row.priceMinor}
							currency={row.currency}
							locked
							min={0}
							disabled={!writable}
							onChange={(priceMinor) => onChange({ priceMinor })}
						/>
					)}
				</FormField>
			</div>
			{sku !== undefined && (
				<div className="flex flex-col gap-2">
					<div className="flex flex-wrap items-center gap-2">
						<ApprovalChip status={sku.approvalStatus} t={t} />
						{sku.approvalStatus === "pending_approval" && writable && (
							<Button
								size="sm"
								variant="secondary"
								disabled={busy}
								onClick={() => act(() => api.approvePortalCatalogSku(sku.skuId))}
							>
								{t("portal.catalog.skus.approve")}
							</Button>
						)}
					</div>
					<PackageSelect
						sku={sku}
						writable={writable && !busy}
						onPick={(packageTypeId) =>
							act(() => api.putPortalSkuPackagingDefault(sku.skuId, packageTypeId))
						}
					/>
					{sku.channels.length > 0 && (
						<ul className="flex flex-col gap-1 text-[12.5px]">
							{sku.channels.map((c) => (
								<li
									key={`${c.channel}:${c.state}:${c.lastSyncedAt ?? ""}`}
									className="flex flex-wrap items-center gap-2"
								>
									<StatusChip tone={CHANNEL_TONE[c.state]} size="sm">
										{t(`portal.catalog.channel.${c.channel}`)} ·{" "}
										{t(`portal.catalog.sync.${c.state}`)}
									</StatusChip>
									{c.lastSyncedAt && (
										<span className="text-fg-muted">
											{formatDateTime(c.lastSyncedAt, { locale, timeZone })}
										</span>
									)}
									{c.error && <span className="text-danger-fg">{c.error}</span>}
								</li>
							))}
						</ul>
					)}
				</div>
			)}
			{problem !== null && (
				<p role="alert" className="text-[12.5px] text-danger-fg">
					{problem}
				</p>
			)}
		</fieldset>
	);
}

/** The package types offered to the store; nothing else can be picked. */
function PackageSelect({
	sku,
	writable,
	onPick,
}: {
	sku: PortalCatalogSku;
	writable: boolean;
	onPick: (packageTypeId: string | null) => void;
}) {
	const { t, locale } = useLocale();
	const api = useApi();
	const { store } = useStoreScope();
	const types = useQuery({
		queryKey: ["portal", store?.storeId ?? "", "packaging-types"],
		queryFn: ({ signal }) => api.getPortalPackagingTypes({ signal }),
	});
	const options = [
		{ value: STORE_DEFAULT, label: t("portal.catalog.package.storeDefault") },
		...(types.data?.items ?? []).map((o) => ({
			value: o.packageTypeId,
			label: locale === "ar" && o.nameAr ? o.nameAr : o.name,
		})),
	];
	return (
		<Field label={t("portal.catalog.fields.package")}>
			{(control) => (
				<Select
					{...control}
					options={options}
					value={sku.defaultPackageTypeId ?? STORE_DEFAULT}
					disabled={!writable}
					onValueChange={(value) => onPick(value === STORE_DEFAULT ? null : value)}
				/>
			)}
		</Field>
	);
}

/** The item's photos: up to four, the first the thumbnail, uploaded as picked. */
function ImagePicker({
	storeId,
	images,
	writable,
	onChange,
	alt,
}: {
	storeId: string;
	images: PortalCatalogImage[];
	writable: boolean;
	onChange: (images: PortalCatalogImage[]) => void;
	alt: string;
}) {
	const { t } = useLocale();
	const api = useApi();
	const inputId = useId();
	const { state, upload } = useUpload({ api, purpose: STORE_ITEM_IMAGE, ownerId: storeId });
	const busy = state.status === "uploading";
	const full = images.length >= MAX_IMAGES;
	return (
		<div className="flex flex-col gap-2">
			<ul className="flex flex-wrap gap-2">
				{images.map((image) => (
					<li key={image.attachmentId} className="relative">
						<Thumbnail image={image} alt={alt} />
						{writable && (
							<button
								type="button"
								aria-label={t("portal.catalog.images.remove")}
								onClick={() =>
									onChange(images.filter((i) => i.attachmentId !== image.attachmentId))
								}
								// A 28px mark with a 44px press area around it (ADJ-M03-4):
								// big enough for a thumb, small enough not to hide the photo.
								className="absolute -end-2 -top-2 grid size-7 place-items-center rounded-full border border-border bg-bg-surface text-[13px] after:absolute after:-inset-2 after:content-['']"
							>
								×
							</button>
						)}
					</li>
				))}
			</ul>
			{writable && (
				<div className="flex items-center gap-2">
					<label
						htmlFor={inputId}
						className={
							busy || full
								? "pointer-events-none rounded-sm border border-border bg-bg-subtle px-3 py-2 text-[13px] text-fg-muted"
								: "cursor-pointer rounded-sm border border-border px-3 py-2 text-[13px] text-fg"
						}
					>
						{busy ? t("portal.catalog.images.uploading") : t("portal.catalog.images.add")}
					</label>
					<input
						id={inputId}
						type="file"
						accept="image/jpeg,image/png,image/webp"
						className="sr-only"
						disabled={busy || full}
						onChange={(event) => {
							const file = event.target.files?.[0];
							event.target.value = "";
							if (file === undefined) {
								return;
							}
							void upload(file).then(
								(a) =>
									onChange([
										...images,
										{ attachmentId: a.id, url: URL.createObjectURL(file), pending: false },
									]),
								() => undefined,
							);
						}}
					/>
					<span className="text-[12px] text-fg-muted">
						{t("portal.catalog.images.hint", { limit: MAX_IMAGES })}
					</span>
				</div>
			)}
			{state.status === "error" && (
				<p role="alert" className="text-[12px] text-danger-fg">
					{t("portal.catalog.images.failed")}
				</p>
			)}
		</div>
	);
}
